import { CSSProperties, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useOS } from '@/lib/os-context';
import { useIsMobile } from '@/hooks/use-mobile';
import { liquidIcon } from '@/lib/liquid-motion';

interface DesktopIconProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}

export function DesktopIcon({ icon, label, onClick }: DesktopIconProps) {
  const { effectiveColorMode, displaySettings } = useOS();
  const isMobile = useIsMobile();
  const isLight = effectiveColorMode === 'light';
  const isTahoe = displaySettings.theme === 'tahoe';
  const iconAccent = getTahoeIconAccent(label);

  return (
    <motion.button
      onClick={onClick}
      whileHover={isTahoe ? liquidIcon.hover : undefined}
      whileTap={isTahoe ? liquidIcon.tap : undefined}
      className={cn(
        "flex flex-col items-center gap-1 rounded-lg group transition-all cursor-pointer text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/80",
        // Mobile: compact grid-style icons
        isMobile ? "p-1.5 w-14" : "p-2 w-24 gap-2",
        isTahoe ? "hover:bg-transparent" : isLight ? "hover:bg-black/10" : "hover:bg-white/10"
      )}
    >
      <div className={cn(
        "rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform border backdrop-blur-md ring-1",
        // Mobile: smaller icons
        isMobile ? "w-10 h-10" : "w-12 h-12",
        isTahoe
          ? "tahoe-app-icon text-white border-white/70 ring-white/50 shadow-[0_12px_24px_rgba(15,23,42,0.24)]"
          : isLight 
          ? "bg-white/95 border-white/90 text-gray-900 shadow-[0_4px_14px_rgba(15,23,42,0.25)] ring-black/5" 
          : "bg-gray-950/85 border-white/20 text-white shadow-[0_4px_16px_rgba(0,0,0,0.45)] ring-white/10"
      )}
        style={isTahoe ? { '--tahoe-icon-accent': iconAccent } as CSSProperties : undefined}
      >
        {icon}
      </div>
      <span
        title={label}
        className={cn(
        "block max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-semibold leading-tight transition-colors",
        // Mobile: smaller text, always one line
        isMobile ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2.5 py-1",
        isTahoe
          ? "tahoe-icon-label rounded-sm"
          : isLight
          ? "rounded-md border backdrop-blur-sm text-gray-950 bg-white/95 border-white/90 shadow-[0_2px_10px_rgba(15,23,42,0.22)] group-hover:bg-white group-hover:shadow-md"
          : "rounded-md border backdrop-blur-sm text-white bg-black/70 border-white/15 shadow-[0_2px_10px_rgba(0,0,0,0.45)] group-hover:bg-black/80"
      )}
      >
        {label}
      </span>
    </motion.button>
  );
}

function getTahoeIconAccent(label: string) {
  const accents: Record<string, string> = {
    Profile: 'linear-gradient(135deg, #60a5fa, #2563eb)',
    'My Notes': 'linear-gradient(135deg, #fde68a, #f59e0b)',
    Projects: 'linear-gradient(135deg, #67e8f9, #3b82f6)',
    Terminal: 'linear-gradient(135deg, #6ee7b7, #059669)',
    Browser: 'linear-gradient(135deg, #93c5fd, #0ea5e9)',
    Contact: 'linear-gradient(135deg, #fdba74, #f97316)',
    'Mini Games': 'linear-gradient(135deg, #d8b4fe, #8b5cf6)',
    Analytics: 'linear-gradient(135deg, #fcd34d, #f59e0b)',
    Achievements: 'linear-gradient(135deg, #fef08a, #eab308)',
    Trash: 'linear-gradient(135deg, #e5e7eb, #94a3b8)',
  };

  return accents[label] ?? 'linear-gradient(135deg, #60a5fa, #38bdf8)';
}
